import React, { useCallback, useEffect, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import {
  fetchWorkspaces,
  WorkspaceResponse,
} from "../../api/workspaces";
import {
  fetchAllCommunityEntities,
  CommunityEntityResponse,
} from "../../api/communityMetadata";
import { mediaSrc } from "../../api/mediaUrl";
import styles from "./WorkspaceListPage.module.css";

interface Props {
  communityGuid: string;
  onOpen: (workspaceGuid: string) => void;
}

const WorkspaceListPage: React.FC<Props> = ({
  communityGuid,
  onOpen,
}) => {
  const { t } = useAppTranslation("pages");
  const [workspaces, setWorkspaces] = useState<WorkspaceResponse[]>([]);
  const [communityName, setCommunityName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ws, entities] = await Promise.all([
        fetchWorkspaces({ communityGuid }),
        fetchAllCommunityEntities(),
      ]);
      setWorkspaces(ws);

      // Resolve community name
      const match = (entities as CommunityEntityResponse[]).find(
        (e) => e.entityGuid.toUpperCase() === communityGuid.toUpperCase()
      );
      setCommunityName(match?.name ?? "");
    } catch (e: any) {
      setError(e?.message ?? "Failed to load workspaces.");
    } finally {
      setLoading(false);
    }
  }, [communityGuid]);

  useEffect(() => {
    load();
  }, [load]);

  const phaseLabel = useCallback(
    (phase: string): string =>
      t(`workspace.phaseLabels.${phase}`) || phase,
    [t]
  );

  const resolveHero = (ws: WorkspaceResponse): string | null => {
    return mediaSrc(ws.detailHeroImageUrl ?? ws.heroImageUrl);
  };

  if (loading) {
    return <div className={styles.loading}>Loading…</div>;
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>{error}</div>
      </div>
    );
  }

  const phaseIndex = (phase: string) => {
    const phases = [
      "signal_detection",
      "detection_validation",
      "analysis",
      "solution_design",
      "resourcing",
      "implementation",
      "sustainment",
    ];
    return phases.indexOf(phase) + 1;
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      {/* DEV-1452: in-app back button removed. */}
      <div className={styles.header}>
        <h1 className={styles.title}>
          {t("workspace.listPage.title", { communityName })}
        </h1>
      </div>

      {/* List */}
      <div className={styles.list}>
        {workspaces.length === 0 && (
          <div className={styles.empty}>
            {t("workspace.listPage.noProblems")}
          </div>
        )}

        {workspaces.map((ws) => {
          const heroUrl = resolveHero(ws);
          const categoryKey = ws.concernCategory
            ? t(`workspace.categoryLabels.${ws.concernCategory}`) || ws.concernCategory
            : null;

          return (
            <button
              key={ws.workspaceGuid}
              type="button"
              className={styles.row}
              onClick={() => onOpen(ws.workspaceGuid)}
            >
              {/* Thumbnail */}
              <div className={styles.thumbnail}>
                {heroUrl ? (
                  <img
                    className={styles.thumbnailImage}
                    src={heroUrl}
                    alt={ws.presentingConcern}
                  />
                ) : (
                  <div className={styles.thumbnailPlaceholder}>
                    <span>📷</span>
                  </div>
                )}
              </div>

              {/* Combined text card */}
              <div className={styles.textCard}>
                {/* Badges row */}
                <div className={styles.badgeRow}>
                  <span className={styles.phaseBadge}>
                    {phaseIndex(ws.phase)}/7 {phaseLabel(ws.phase)}
                  </span>
                  {categoryKey && (
                    <span className={styles.categoryBadge}>
                      {categoryKey}
                    </span>
                  )}
                </div>

                {/* Presenting Concern section */}
                <div className={styles.sectionHeader}>
                  {t("workspace.listPage.presentingConcern")}
                </div>
                <div className={styles.sectionBody}>
                  {ws.description || ws.presentingConcern}
                </div>

                {/* Summary Findings section (if available) */}
                {ws.summaryFindings && (
                  <>
                    <div className={styles.sectionDivider} />
                    <div className={styles.sectionHeader}>
                      {t("workspace.listPage.summaryFindings")}
                      <span className={styles.aiBadge}>AI + Human</span>
                    </div>
                    <div className={styles.sectionBody}>
                      {ws.summaryFindings}
                    </div>
                  </>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default WorkspaceListPage;
